import React, { useState } from 'react';
import { applyPartner } from '../../lib/api';

const initial = {
  name: '',
  property_type: 'hotel',
  rooms: 50,
  contact_name: '',
  contact_email: '',
  location: '',
};

const ApplyPartnerForm = ({ onApplied }) => {
  const [form, setForm] = useState(initial);
  const [state, setState] = useState({ loading: false, ok: null, err: '' });

  const update = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: k === 'rooms' ? Number(e.target.value) || 0 : e.target.value,
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setState({ loading: true, ok: null, err: '' });
    try {
      const res = await applyPartner(form);
      setState({ loading: false, ok: res, err: '' });
      setForm(initial);
      if (onApplied) onApplied(res);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not submit. Please try again.';
      setState({
        loading: false,
        ok: null,
        err: typeof detail === 'string' ? detail : 'Please check your entries.',
      });
    }
  };

  if (state.ok) {
    return (
      <div
        data-testid="partner-apply-success"
        className="border border-[#F4F8F9]/40 p-8 bg-[#F4F8F9]/10 backdrop-blur-md"
      >
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F8F9]/80 mb-3">
          Application received
        </p>
        <h3 className="font-serif text-3xl font-light">Welcome, {state.ok.name}.</h3>
        <p className="mt-3 text-[#F4F8F9]/90 font-light leading-relaxed">
          You have been registered as a <em>{state.ok.tier}</em> partner.
          Your status is <em>{state.ok.status}</em>. Your private API key and SBT
          certificate are issued once our team approves the application.
        </p>
        <div className="mt-5 font-mono text-xs bg-[#F4F8F9]/10 border border-[#F4F8F9]/30 px-4 py-3">
          <div
            data-testid="partner-apply-id"
            className="truncate tracking-[0.12em]"
          >
            Partner ID · {state.ok.id}
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="partner-apply-form"
      className="space-y-5 bg-[#F4F8F9]/10 backdrop-blur-md p-8 border border-[#F4F8F9]/30"
    >
      <Field label="Property name">
        <input
          required
          value={form.name}
          onChange={update('name')}
          data-testid="partner-apply-name"
          className="partner-input"
        />
      </Field>
      <div className="grid grid-cols-2 gap-5">
        <Field label="Property type">
          <select
            value={form.property_type}
            onChange={update('property_type')}
            data-testid="partner-apply-type"
            className="partner-input"
          >
            {['hotel', 'spa', 'retreat', 'salon', 'residence'].map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Rooms / keys">
          <input
            type="number"
            min={1}
            max={5000}
            value={form.rooms}
            onChange={update('rooms')}
            data-testid="partner-apply-rooms"
            className="partner-input"
          />
        </Field>
      </div>
      <Field label="Contact name">
        <input
          required
          value={form.contact_name}
          onChange={update('contact_name')}
          data-testid="partner-apply-contact"
          className="partner-input"
        />
      </Field>
      <Field label="Email">
        <input
          type="email"
          required
          value={form.contact_email}
          onChange={update('contact_email')}
          data-testid="partner-apply-email"
          className="partner-input"
        />
      </Field>
      <Field label="Location (city, country)">
        <input
          value={form.location}
          onChange={update('location')}
          data-testid="partner-apply-location"
          className="partner-input"
        />
      </Field>

      <button
        type="submit"
        disabled={state.loading}
        data-testid="partner-apply-submit"
        className="bg-[#F4F8F9] text-[#7C9CB1] px-8 py-4 text-[11px] tracking-[0.22em] uppercase hover:bg-[#C1D7E2] transition-colors disabled:opacity-60"
      >
        {state.loading ? 'Sending…' : 'Submit application'}
      </button>
      {state.err && (
        <p data-testid="partner-apply-error" className="text-sm text-[#F4F8F9]/90">
          {state.err}
        </p>
      )}
      <style>{`
        .partner-input {
          width: 100%;
          background: transparent;
          border: 0;
          border-bottom: 1px solid rgba(244,248,249,0.4);
          color: #F4F8F9;
          font-family: 'Outfit', sans-serif;
          font-weight: 300;
          padding: 10px 2px;
          font-size: 15px;
        }
        .partner-input option { color: #7C9CB1; }
        .partner-input::placeholder { color: rgba(244,248,249,0.45); }
        .partner-input:focus { outline: none; border-bottom-color: #F4F8F9; }
      `}</style>
    </form>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F8F9]/80">
      {label}
    </span>
    <div className="mt-2">{children}</div>
  </label>
);

export default ApplyPartnerForm;
